/**
 * Skunked and Stripped (Adult Brand) Variant Manager
 * Manages strip options, timed adult challenges, and timer modal pause/resume orchestration.
 */

export const ADULT_OPTIONS_DEFS = Object.freeze([
  // Group 1: Strip Themed
  { key: 'loseClothing', chkId: 'optLoseClothing', name: 'You Lose Clothing', actionText: 'Player loses an article of clothing', group: 'strip', icon: '👙', color: '#f43f5e', defaultHoles: [10, 70, 10, 70], timed: false },
  { key: 'gainClothing', chkId: 'optGainClothing', name: 'You Gain Clothing', actionText: 'Player gains an article of clothing back', group: 'strip', icon: '🧥', color: '#10b981', defaultHoles: [20, 80, 20, 80], timed: false },
  { key: 'pickWhoLosesClothing', chkId: 'optPickWhoLosesClothing', name: 'Pick Who Strips', actionText: 'Player picks who loses an article of clothing', group: 'strip', icon: '🎯', color: '#ec4899', defaultHoles: [30, 90, 30, 90], timed: false },
  { key: 'rightLosesClothing', chkId: 'optRightLosesClothing', name: 'Right Neighbor Strips', actionText: 'Person to right loses an article of clothing', group: 'strip', icon: '👉', color: '#3b82f6', defaultHoles: [40, 100, 40, 100], timed: false },
  { key: 'leftLosesClothing', chkId: 'optLeftLosesClothing', name: 'Left Neighbor Strips', actionText: 'Person to left loses an article of clothing', group: 'strip', icon: '👈', color: '#8b5cf6', defaultHoles: [50, 110, 50, 110], timed: false },
  { key: 'removeClothingChoice', chkId: 'optRemoveClothingChoice', name: 'Remove Clothing Choice', actionText: 'Remove an article of clothing off person of your choice', group: 'strip', icon: '✂️', color: '#e11d48', defaultHoles: [60, 120, 60, 120], timed: false },

  // Group 2: Adult Themed (Timed Challenges)
  { key: 'touchBreast', chkId: 'optTouchBreast', name: 'Touch Breast', actionText: 'Touch breast', group: 'adult', icon: '💋', color: '#f43f5e', defaultHoles: [5, 45, 75, 105], timed: true },
  { key: 'lickBreast', chkId: 'optLickBreast', name: 'Lick Breast', actionText: 'Lick breast', group: 'adult', icon: '👅', color: '#ec4899', defaultHoles: [15, 50, 80, 110], timed: true },
  { key: 'suckBreast', chkId: 'optSuckBreast', name: 'Suck Breast', actionText: 'Suck breast', group: 'adult', icon: '👄', color: '#d946ef', defaultHoles: [25, 55, 85, 115], timed: true },
  { key: 'touchGenitals', chkId: 'optTouchGenitals', name: 'Touch Genitals', actionText: 'Touch genitals', group: 'adult', icon: '✨', color: '#a855f7', defaultHoles: [35, 40, 70, 100], timed: true },
  { key: 'lickGenitals', chkId: 'optLickGenitals', name: 'Lick Genitals', actionText: 'Lick genitals', group: 'adult', icon: '👅', color: '#f43f5e', defaultHoles: [45, 60, 90, 120], timed: true },
  { key: 'suckGenitals', chkId: 'optSuckGenitals', name: 'Suck Genitals', actionText: 'Suck genitals', group: 'adult', icon: '👄', color: '#ec4899', defaultHoles: [55, 65, 95, 115], timed: true },
  { key: 'fingerGenitals', chkId: 'optFingerGenitals', name: 'Finger Genitals', actionText: 'Finger genitals', group: 'adult', icon: '👉', color: '#f59e0b', defaultHoles: [65, 45, 75, 105], timed: true }
]);

export class AdultManager {
  constructor(options = {}) {
    this.enabled = Boolean(options.enabled);
    this.options = { ...options };
    this.activeHoles = new Map();
    this.timerInterval = null;
    this.rebuildHoleMap();
  }

  rebuildHoleMap(settings = this.options) {
    this.activeHoles.clear();
    for (const def of ADULT_OPTIONS_DEFS) {
      if (settings && (settings[def.key] || settings[def.chkId])) {
        const holes = settings[`${def.key}Holes`] || def.defaultHoles;
        for (const h of holes) {
          if (h > 0) this.activeHoles.set(h, def);
        }
      }
    }
  }

  checkHole(holeNum) {
    return this.activeHoles.get(holeNum) || null;
  }

  /**
   * Retrieves definition for an adult option by its key.
   * @param {string} key
   * @param {Array} [optionsList]
   * @returns {Object|null}
   */
  getOptionDef(key, optionsList = ADULT_OPTIONS_DEFS) {
    return optionsList.find(o => o.key === key) || null;
  }

  /**
   * Generates default configuration for the Adult / Skunked and Stripped brand.
   * @param {Array} [optionsList]
   * @returns {Object}
   */
  getDefaultSettings(optionsList = ADULT_OPTIONS_DEFS) {
    const cfg = {
      target: 121,
      goal: 'high',
      theme: 'passion'
    };
    optionsList.forEach(opt => {
      const isStrip = opt.group === 'strip';
      const defaultCount = isStrip ? 2 : 1;
      cfg[opt.chkId] = true;
      cfg[opt.key + 'Count'] = defaultCount;
      cfg[opt.key + 'Pegs'] = 1;
      cfg[opt.key + 'Holes'] = [...opt.defaultHoles];
    });
    return cfg;
  }

  /**
   * Retrieves all active adult option configs across strip and timed challenges.
   * @param {Object} settings
   * @param {Array} [optionsList]
   * @returns {Array}
   */
  getAllActiveConfigs(settings, optionsList = ADULT_OPTIONS_DEFS) {
    if (!settings) return [];
    const all = [];
    optionsList.forEach(opt => {
      if (settings[opt.chkId] || settings[opt.key]) {
        const count = Math.min(4, Math.max(1, settings[opt.key + 'Count'] != null ? parseInt(settings[opt.key + 'Count'], 10) : (opt.group === 'strip' ? 2 : 1)));
        const pegs = Math.min(5, Math.max(1, parseInt(settings[opt.key + 'Pegs'], 10) || 1));
        const holes = settings[opt.key + 'Holes'] || opt.defaultHoles;
        for (let i = 0; i < count; i++) {
          const h = holes[i] != null ? holes[i] : opt.defaultHoles[i];
          all.push({
            id: i + 1,
            hole: h,
            start: h,
            end: h + pegs - 1,
            pegs,
            type: opt.key,
            label: `${opt.name} ${i + 1}`,
            item: opt
          });
        }
      }
    });
    return all;
  }

  /**
   * Resolves whether a specific board hole triggers an active Adult space.
   * @param {number} hole
   * @param {Object} settings
   * @param {Array} [optionsList]
   * @param {string} [brand='adult']
   * @returns {Object|null}
   */
  getActiveOptionAtHole(hole, settings, optionsList = ADULT_OPTIONS_DEFS, brand = 'adult') {
    if (!settings) return null;
    if (brand !== 'adult') return null;
    const list = optionsList || ADULT_OPTIONS_DEFS;
    const all = this.getAllActiveConfigs(settings, list);
    return all.find(c => hole >= c.start && hole <= c.end) || null;
  }

  /**
   * Clears all adult option checkboxes.
   * @param {Array} [optionsList]
   * @returns {Object}
   */
  clearAll(optionsList = ADULT_OPTIONS_DEFS) {
    const cleared = {};
    optionsList.forEach(opt => {
      cleared[opt.chkId] = false;
      cleared[opt.key] = false;
    });
    return cleared;
  }

  stopTimer() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }
}
